// src/components/padrekko/qc/CloseCaseModal.jsx
//
// Phase 2 — customer-close modal. Triggered from the "Close case" button on
// CaseBanner (visible only to the customer_analyst at customer_review with
// operator_pass_count > 0). Captures an OPTIONAL reason, then calls
// useQcStore.closeCase. Backend re-validates the guards; this modal trusts the
// parent button's visibility gate.

import { useState } from "react";
import { Loader2, X as XIcon, AlertCircle, CheckCircle2 } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import HandoffWarningsModal from "./HandoffWarningsModal";

export default function CloseCaseModal({ isOpen, onClose }) {
  const caseInfo      = useQcStore((s) => s.caseInfo);
  const closeCase     = useQcStore((s) => s.closeCase);
  const closeInflight = useQcStore((s) => s.closeInflight);

  const [reason, setReason] = useState("");
  const [inlineError, setInlineError] = useState(null);
  const [handoffWarnings, setHandoffWarnings] = useState(null);

  if (!isOpen || !caseInfo) return null;

  function handleCancel() {
    if (closeInflight) return;
    setReason("");
    setInlineError(null);
    onClose();
  }

  // The soft disposition gate. The first attempt carries no acknowledgment, so
  // the server refuses once with the count; the retry carries it and always
  // proceeds. Closing runs the SAME gate as forwarding — two doors to
  // `complete` with different rules is the divergence this deliberately avoids.
  async function submitClose(acknowledge) {
    setInlineError(null);
    const result = await closeCase({
      reason: reason.trim() || undefined,
      acknowledgeHandoff: acknowledge,
    });
    if (result.ok) {
      setHandoffWarnings(null);
      setReason("");
      onClose();
      return;
    }
    if (result.handoffWarnings) {
      setHandoffWarnings(result.handoffWarnings);
      return;
    }
    setHandoffWarnings(null);
    setInlineError(result.error || "Close failed.");
  }

  const handleSubmit = () => submitClose(false);

  return (
    <>
    <HandoffWarningsModal
      isOpen={handoffWarnings !== null}
      detail={handoffWarnings}
      action="close"
      busy={closeInflight}
      onConfirm={() => submitClose(true)}
      onClose={() => setHandoffWarnings(null)}
    />
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleCancel()}
    >
      <div className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100 flex items-center gap-1.5">
            <CheckCircle2 size={12} className="text-emerald-400" />
            Close case?
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            disabled={closeInflight}
            className="text-fg-secondary hover:text-slate-200 transition disabled:opacity-40"
            title="Cancel"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-[11px] text-slate-300 leading-relaxed">
            This case will be marked <span className="text-emerald-300 font-medium">complete</span> and
            become read-only across all participants. Reopening requires a DreamHammer admin.
          </p>

          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-fg-secondary">
              Reason (optional)
            </label>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (inlineError) setInlineError(null);
              }}
              disabled={closeInflight}
              rows={3}
              placeholder="Optional note recorded with the closure."
              className="w-full text-[11px] px-2.5 py-2 rounded border
                         border-slate-700 bg-slate-900 text-slate-100
                         placeholder:text-slate-600
                         focus:outline-none focus:border-slate-500
                         disabled:opacity-50 leading-snug resize-none"
            />
          </div>

          {inlineError && (
            <div className="flex items-start gap-1.5 text-[10px] text-red-300 leading-snug">
              <AlertCircle size={10} className="mt-0.5 shrink-0" />
              <span>{inlineError}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end gap-1.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={closeInflight}
            className="text-[11px] px-3 py-1.5 rounded border
                       border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition
                       disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={closeInflight}
            className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded border
                       bg-emerald-500/15 border-emerald-500/50 text-emerald-200
                       hover:bg-emerald-500/25
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {closeInflight && <Loader2 size={11} className="animate-spin" />}
            {closeInflight ? "Closing…" : "Close case"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
